import axios, { AxiosInstance } from 'axios';
import { toast } from 'react-hot-toast';
import {
  User,
  LoginRequest,
  SignupRequest,
  TokenResponse,
  Thread,
  Message,
  QueryRequest,
  IngestResponse,
  Document,
  Folder,
  FolderCreateRequest,
  FolderUpdateRequest,
  FolderContextOut,
  FolderContextAddRequest,
  DataSource,
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Default to localhost, can be overridden with environment variable
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: on 401 try refresh token, then retry; only redirect to login if refresh fails
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const config = error.config;

        if (status === 401) {
          const isRefreshEndpoint = config?.url?.includes('auth/refresh');
          if (isRefreshEndpoint || config?.__retriedAfterRefresh) {
            this.clearAuthTokens();
            window.dispatchEvent(new CustomEvent('auth:logout'));
            if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
              toast.error('Your session has expired. Please log in again.');
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          const refreshToken = this.getRefreshToken();
          if (refreshToken) {
            try {
              const tokenResponse = await this.doRefresh(refreshToken);
              this.setAuthTokens(tokenResponse.access_token, tokenResponse.refresh_token ?? null);
              window.dispatchEvent(
                new CustomEvent('auth:tokens-refreshed', {
                  detail: {
                    access_token: tokenResponse.access_token,
                    refresh_token: tokenResponse.refresh_token ?? null,
                  },
                })
              );
              config.headers = config.headers || {};
              config.headers.Authorization = `Bearer ${tokenResponse.access_token}`;
              config.__retriedAfterRefresh = true;
              return this.api.request(config);
            } catch (refreshErr) {
              // Refresh failed; fall through to clear and redirect
            }
          }

          this.clearAuthTokens();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            toast.error('Your session has expired. Please log in again.');
            window.location.href = '/login';
          }
        } else if (status === 429 && error.response?.data?.detail?.error === 'rate_limit_exceeded') {
          // Rate limit: no toast here; caller or RateLimitExceededModal will show modal
        } else if (status >= 500) {
          toast.error('Server error. Please try again later.');
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        }
        return Promise.reject(error);
      }
    );
  }

  /** Call POST /auth/refresh. 401 on this request is handled by interceptor (no retry loop). */
  private async doRefresh(refreshToken: string): Promise<TokenResponse> {
    const { data } = await this.api.post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken });
    return data;
  }

  // Health check
  async healthCheck(): Promise<{ ok: boolean }> {
    const response = await this.api.get('/healthz');
    return response.data;
  }

  // Get application config
  async getConfig(): Promise<{ app_name: string }> {
    const response = await this.api.get('/config');
    return response.data;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await this.api.post('/auth/login', credentials);
    return response.data;
  }

  // Get current user profile
  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/users/me');
    return response.data;
  }

  // Update current user profile
  async updateUserProfile(data: { display_name?: string; country?: string; description?: string; default_skill_id?: string }): Promise<User> {
    const response = await this.api.put('/users/me', data);
    return response.data;
  }

  async signup(userData: SignupRequest): Promise<User> {
    const response = await this.api.post('/users/signup', userData);
    return response.data;
  }

  // Password reset endpoints
  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  }

  async verifyResetToken(token: string): Promise<{ valid: boolean }> {
    const response = await this.api.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  }

  // Thread endpoints
  async getThreads(noFolder?: boolean, folderId?: string): Promise<Thread[]> {
    const params = new URLSearchParams();
    if (noFolder) params.append('no_folder', 'true');
    if (folderId) params.append('folder_id', folderId);
    
    const response = await this.api.get(`/threads?${params.toString()}`);
    return response.data;
  }

  async getThread(threadId: string): Promise<Thread> {
    const response = await this.api.get(`/threads/${threadId}`);
    return response.data;
  }

  async getThreadHistory(threadId: string, limit: number = 50): Promise<Message[]> {
    const response = await this.api.get(`/history?thread_id=${threadId}&limit=${limit}`);
    return response.data;
  }

  // Thread management endpoints
  async createThread(question: string, folderId?: string): Promise<Thread> {
    const response = await this.api.post('/threads', {
      question,
      folder_id: folderId
    });
    return response.data;
  }

  async updateThread(threadId: string, updateData: { title?: string; folder_id?: string | null; skill_id?: string }): Promise<Thread> {
    const response = await this.api.put(`/threads/${threadId}`, updateData);
    return response.data;
  }

  async deleteThread(threadId: string): Promise<void> {
    await this.api.delete(`/threads/${threadId}`);
  }

  // Query endpoint (streaming)
  async sendQuery(queryData: QueryRequest): Promise<ReadableStream> {
    const authToken = this.getAuthToken();
    console.log('🔑 Auth token:', authToken ? `${authToken.substring(0, 20)}...` : 'NO TOKEN');
    console.log('🌐 API Base URL:', this.baseURL);
    
    const response = await fetch(`${this.baseURL}/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(queryData),
    });

    console.log('📡 Response status:', response.status, response.statusText);
    console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      throw new Error(`Query failed: ${response.statusText}`);
    }

    return response.body!;
  }

  // File upload endpoints
  async uploadFile(file: File): Promise<IngestResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.api.post('/ingest/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Document upload with context (thread/folder association) - streaming SSE
  async uploadDocumentStream(
    file: File,
    threadId?: string,
    folderId?: string,
    options?: { isGlobal?: boolean }
  ): Promise<ReadableStream<Uint8Array>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (threadId) {
      formData.append('thread_id', threadId);
    }
    if (folderId) {
      formData.append('folder_id', folderId);
    }
    if (options?.isGlobal) {
      formData.append('is_global', 'true');
    }

    const authToken = this.getAuthToken();
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${this.baseURL}/documents/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Upload failed: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Upload response did not include a body stream.');
    }

    return response.body;
  }

  async uploadUrlDocumentStream(
    url: string,
    threadId?: string,
    folderId?: string,
    title?: string
  ): Promise<ReadableStream<Uint8Array>> {
    const payload: Record<string, unknown> = { url };
    if (threadId) payload.thread_id = threadId;
    if (folderId) payload.folder_id = folderId;
    if (title) payload.title = title;

    const authToken = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${this.baseURL}/documents/url`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `URL upload failed: ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('URL upload response did not include a body stream.');
    }

    return response.body;
  }

  async uploadUrlDocument(
    url: string,
    threadId?: string,
    folderId?: string,
    title?: string
  ): Promise<{ doc_id: string; title: string; filename: string; chunks: number; status: string }> {
    console.log('📡 [API] uploadUrlDocument called', {
      url,
      threadId,
      folderId,
      title,
      baseURL: this.baseURL,
      hasAuthToken: !!this.getAuthToken()
    });

    const payload: Record<string, unknown> = { url };
    if (threadId) payload.thread_id = threadId;
    if (folderId) payload.folder_id = folderId;
    if (title) payload.title = title;

    console.log('📡 [API] Request payload', payload);
    console.log('📡 [API] Making POST request to /documents/url');

    try {
      const response = await this.api.post('/documents/url', payload);
      console.log('✅ [API] uploadUrlDocument success', {
        status: response.status,
        data: response.data
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ [API] uploadUrlDocument error', {
        url,
        error,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers
      });
      throw error;
    }
  }

  // Get documents list
  async getDocuments(
    threadId?: string,
    folderId?: string,
    options?: { isGlobal?: boolean; dataSourceId?: string; folderType?: 'knowledge_base' }
  ): Promise<Document[]> {
    const params = new URLSearchParams();
    if (threadId) params.append('thread_id', threadId);
    if (folderId) params.append('folder_id', folderId);
    if (options?.isGlobal) params.append('is_global', 'true');
    if (options?.dataSourceId) params.append('data_source_id', options.dataSourceId);
    if (options?.folderType === 'knowledge_base') params.append('folder_type', 'knowledge_base');

    const query = params.toString();
    const response = await this.api.get(`/documents${query ? `?${query}` : ''}`);
    return response.data;
  }

  /** Ingest PDF from URL only (path removed for security). Requires authentication. */
  async ingestPdf(url: string, chunkSize?: number, chunkOverlap?: number): Promise<IngestResponse> {
    const response = await this.api.post('/ingest/pdf', {
      url,
      chunk_size: chunkSize,
      chunk_overlap: chunkOverlap,
    });
    return response.data;
  }

  // Document endpoints
  async getDocument(docId: string): Promise<Document> {
    const response = await this.api.get(`/doc/${docId}`);
    return response.data;
  }

  async downloadDocument(docId: string): Promise<Blob> {
    const authToken = this.getAuthToken();
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${this.baseURL}/documents/${docId}/download`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.statusText}`);
    }

    return await response.blob();
  }

  async deleteDocument(docId: string): Promise<void> {
    await this.api.delete(`/documents/${docId}`);
  }

  async updateDocumentSummary(docId: string, summaryText: string): Promise<Document> {
    const response = await this.api.patch(`/documents/${docId}/summary`, {
      summary_text: summaryText,
    });
    return response.data;
  }

  async associateDocumentWithFolders(docId: string, folderIds: string[]): Promise<Document> {
    const response = await this.api.post(`/documents/${docId}/folders`, {
      folder_ids: folderIds,
    });
    return response.data;
  }

  async disassociateDocumentFromFolder(docId: string, folderId: string): Promise<Document> {
    const response = await this.api.delete(`/documents/${docId}/folders/${folderId}`);
    return response.data;
  }

  async bulkAssociateDocumentsWithFolders(docIds: string[], folderIds: string[]): Promise<{ success_count: number; failed_count: number; results: Array<{ doc_id: string; success: boolean; error?: string }> }> {
    const response = await this.api.post('/documents/bulk-associate-folders', {
      doc_ids: docIds,
      folder_ids: folderIds,
    });
    return response.data;
  }

  // Folder endpoints. folderType: 'user' | 'global' | 'data_source' (data_source = folders with data_source_id set); omit for user+global.
  async getFolders(flat: boolean = false, folderType?: 'user' | 'global' | 'knowledge_base' | 'data_source'): Promise<Folder[]> {
    let url = `/folders?flat=${flat}`;
    if (folderType) {
      url += `&folder_type=${encodeURIComponent(folderType)}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async createFolder(folderData: FolderCreateRequest): Promise<Folder> {
    const response = await this.api.post('/folders', folderData);
    return response.data;
  }

  async updateFolder(folderId: string, updateData: FolderUpdateRequest): Promise<Folder> {
    const response = await this.api.put(`/folders/${folderId}`, updateData);
    return response.data;
  }

  async deleteFolder(folderId: string, force: boolean = false): Promise<void> {
    // Ensure force is sent as lowercase string for FastAPI boolean query parameter parsing
    await this.api.delete(`/folders/${folderId}?force=${String(force).toLowerCase()}`);
  }

  // Folder context (add folder or data source as context to a folder)
  async getFolderContext(folderId: string): Promise<FolderContextOut[]> {
    const response = await this.api.get(`/folders/${folderId}/context`);
    return response.data;
  }

  async addFolderContext(folderId: string, payload: FolderContextAddRequest): Promise<FolderContextOut> {
    const response = await this.api.post(`/folders/${folderId}/context`, payload);
    return response.data;
  }

  async removeFolderContext(folderId: string, contextId: string): Promise<void> {
    await this.api.delete(`/folders/${folderId}/context/${contextId}`);
  }

  // Data sources (Google Drive)
  async getDataSourceConnectors(): Promise<{ connectors: Array<{ id: string; name: string; description: string; auth_type: string }> }> {
    const response = await this.api.get('/data-sources/connectors');
    return response.data;
  }

  async getGoogleDriveConnectUrl(
    parentFolderId?: string,
    asKnowledgeBase?: boolean,
    returnOrigin?: string
  ): Promise<string> {
    const params = new URLSearchParams();
    if (parentFolderId) params.set('parent_folder_id', parentFolderId);
    if (asKnowledgeBase) params.set('as_knowledge_base', 'true');
    if (returnOrigin) params.set('return_origin', returnOrigin);
    const q = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/data-sources/connect/google_drive${q}`);
    return response.data.auth_url;
  }

  async getGoogleSearchConsoleConnectUrl(
    parentFolderId?: string,
    asKnowledgeBase?: boolean,
    returnOrigin?: string
  ): Promise<string> {
    const params = new URLSearchParams();
    if (parentFolderId) params.set('parent_folder_id', parentFolderId);
    if (asKnowledgeBase) params.set('as_knowledge_base', 'true');
    if (returnOrigin) params.set('return_origin', returnOrigin);
    const q = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/data-sources/connect/google_search_console${q}`);
    return response.data.auth_url;
  }

  /** Get OAuth connect URL. Pass returnOrigin (e.g. window.location.origin) so post-OAuth redirect goes to the same domain. */
  async getConnectUrl(
    connectorId: string,
    parentFolderId?: string,
    asKnowledgeBase?: boolean,
    returnOrigin?: string
  ): Promise<string> {
    if (connectorId === 'google_drive') return this.getGoogleDriveConnectUrl(parentFolderId, asKnowledgeBase, returnOrigin);
    if (connectorId === 'google_search_console') return this.getGoogleSearchConsoleConnectUrl(parentFolderId, asKnowledgeBase, returnOrigin);
    throw new Error(`No connect URL for connector: ${connectorId}`);
  }

  async getGscSites(credentialId: string): Promise<{ sites: Array<{ siteUrl: string; permissionLevel?: string }> }> {
    const params = new URLSearchParams({ credential_id: credentialId });
    const response = await this.api.get(`/data-sources/gsc/sites?${params}`);
    return response.data;
  }

  async getDriveFolders(credentialId: string, parentId?: string, pageToken?: string): Promise<{ folders: Array<{ id: string; name: string; parents: string[] }>; next_page_token?: string }> {
    const params = new URLSearchParams({ credential_id: credentialId });
    if (parentId) params.append('parent_id', parentId);
    if (pageToken) params.append('page_token', pageToken);
    const response = await this.api.get(`/data-sources/drive/folders?${params}`);
    return response.data;
  }

  async createDataSource(body: {
    connector_type: string;
    parent_folder_id?: string;
    as_knowledge_base?: boolean;
    display_name?: string;
    credential_id: string;
    drive_folder_id?: string;
    drive_folder_name?: string;
    site_url?: string;
    date_range_days?: number;
    display_site_url?: string;
  }): Promise<DataSource> {
    const response = await this.api.post('/data-sources', body);
    return response.data;
  }

  async getDataSources(): Promise<{ data_sources: DataSource[] }> {
    const response = await this.api.get('/data-sources');
    return response.data;
  }

  async getDataSource(id: string): Promise<DataSource> {
    const response = await this.api.get(`/data-sources/${id}`);
    return response.data;
  }

  async updateDataSource(id: string, body: { display_name?: string }): Promise<DataSource> {
    const response = await this.api.patch(`/data-sources/${id}`, body);
    return response.data;
  }

  async deleteDataSource(id: string): Promise<void> {
    await this.api.delete(`/data-sources/${id}`);
  }

  async triggerDataSourceSync(id: string): Promise<{ status: string; job_id: string }> {
    const response = await this.api.post(`/data-sources/${id}/sync`);
    return response.data;
  }

  async getDataSourceSyncStatus(id: string): Promise<{
    status: string;
    last_sync_at?: string;
    last_sync_error?: string;
    sync_progress?: { total: number; processed: number; synced: number; skipped: number; errors: number; percent: number | null } | null;
  }> {
    const response = await this.api.get(`/data-sources/${id}/sync/status`);
    return response.data;
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  setAuthTokens(accessToken: string, refreshToken: string | null): void {
    localStorage.setItem('auth_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('auth_refresh_token', refreshToken);
    } else {
      localStorage.removeItem('auth_refresh_token');
    }
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('auth_refresh_token');
  }

  clearAuthTokens(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('user');
  }

  removeAuthToken(): void {
    this.clearAuthTokens();
  }

  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  // Contact Form
  async submitContact(data: {
    name: string;
    mobile: string;
    email: string;
    company: string;
    need_document_ai_for: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/contact', data);
    return response.data;
  }

  // Skills endpoints
  async getSkills(): Promise<{ skills: Array<{ skill_id: string; name: string; description: string; category: string; domain: string; tone: string; is_default: boolean }> }> {
    const response = await this.api.get('/skills');
    return response.data;
  }

  // Usage endpoints
  async getUsageSummary(): Promise<{
    id: string;
    user_id: string;
    package_id: string;
    period_start: string;
    period_end: string;
    token_usage_total: number;
    token_usage_limit: number | null;
    storage_usage_bytes: number;
    storage_limit_bytes: number | null;
    last_updated: string;
  }> {
    const response = await this.api.get('/usage/me');
    return response.data;
  }

  // Update thread skill_id
  async updateThreadSkill(threadId: string, skillId: string): Promise<Thread> {
    const response = await this.api.put(`/threads/${threadId}`, { skill_id: skillId });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
